/* ============================================================
   URL ROUTING — /channel/<id>/<pos>
   ------------------------------------------------------------
   READ once on boot (overrides localStorage so a shared link starts
   watching immediately), WRITE-only afterwards (history.replaceState,
   no navigation). An invalid/unknown URL is ignored → normal boot.

   <id>  = chNN (zero-pad tolerant) for numbered channels, or a slug
           (e.g. /channel/hideo) for name-routed ones.
   <pos> = 'live' (or omitted) | a program ordinal (0-based).
   ============================================================ */
import { queueIndexToProgramOrdinal } from './breakResolver'
import type { Channel, TvState } from './types'

export interface RouteTarget {
  channelNum: number
  programOrdinal: number
  live: boolean
}

/** Channels addressed by slug instead of chNN (the rest are /channel/chNN). */
const NAME_ROUTED = new Set(['hideo'])

const pad2 = (n: number) => (n >= 0 && n < 10 ? '0' : '') + n

function resolveChannel(id: string, channels: Channel[]): Channel | null {
  const m = id.match(/^ch0*(\d+)$/)
  if (m) return channels.find((c) => c.number === Number(m[1])) ?? null
  if (/^\d+$/.test(id)) return channels.find((c) => c.number === Number(id)) ?? null
  return channels.find((c) => (c.slug || '').toLowerCase() === id) ?? null
}

/** Parse `/channel/<id>/<pos?>` into a route target, or null when the path / channel / position is
 *  malformed or unknown (the caller then boots from localStorage as usual). */
export function parseUrl(pathname: string, channels: Channel[]): RouteTarget | null {
  const m = pathname.match(/^\/channel\/([^/]+)(?:\/([^/]+))?\/?$/)
  if (!m) return null
  const ch = resolveChannel(decodeURIComponent(m[1]).toLowerCase(), channels)
  if (!ch) return null
  const pos = m[2] == null ? '' : decodeURIComponent(m[2]).toLowerCase()
  if (pos === '' || pos === 'live') return { channelNum: ch.number, programOrdinal: 0, live: true }
  const n = Number(pos)
  if (!Number.isInteger(n) || n < 0 || String(n) !== pos) return null // reject 'live3', '-1', '03', etc.
  return { channelNum: ch.number, programOrdinal: n, live: false }
}

/** The address-bar URL for a channel + position. Name-routed channels (e.g. hideo) are just
 *  `/channel/<slug>` (no meaningful position); numbered channels are `/channel/chNN/<pos>`. */
export function urlFor(ch: Channel, live: boolean, programOrdinal: number): string {
  if (NAME_ROUTED.has((ch.slug || '').toLowerCase())) return `/channel/${ch.slug}`
  return `/channel/ch${pad2(ch.number)}/${live ? 'live' : Math.max(0, programOrdinal)}`
}

/** WRITE-only: reflect the current channel/position in the address bar (no navigation). Power-off ⇒ `/`. */
export function syncUrl(state: TvState, channels: Channel[]): void {
  if (typeof window === 'undefined') return
  let path = '/'
  if (state.power === 'on') {
    const ch = channels[state.channelIndex]
    if (ch) {
      const ordinal = state.queue.length ? queueIndexToProgramOrdinal(state.queue, state.programIndex) : state.programIndex
      path = urlFor(ch, state.live, ordinal)
    }
  }
  try {
    if (window.location.pathname !== path) window.history.replaceState(null, '', path)
  } catch {
    /* history unavailable; ignore */
  }
}
